import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for mobile gesture handling
 */
export const useMobileGestures = (options = {}) => {
  const {
    onSwipeLeft = null,
    onSwipeRight = null,
    onSwipeUp = null,
    onSwipeDown = null,
    onPinch = null,
    onTap = null,
    onLongPress = null,
    swipeThreshold = 50,
    longPressDelay = 500,
    preventDefault = true
  } = options;

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const elementRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (preventDefault) e.preventDefault();
    
    const touch = e.touches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });
    setTouchEnd(null);

    // Long press detection
    if (onLongPress) {
      const timer = setTimeout(() => {
        onLongPress(e);
        // Haptic feedback for long press
        if ('vibrate' in navigator) {
          navigator.vibrate(100);
        }
      }, longPressDelay);
      setLongPressTimer(timer);
    }
  }, [onLongPress, longPressDelay, preventDefault]);

  const handleTouchMove = useCallback((e) => {
    if (preventDefault) e.preventDefault();
    
    const touch = e.touches[0];
    setTouchEnd({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });

    // Cancel long press if moved
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  }, [longPressTimer, preventDefault]);

  const handleTouchEnd = useCallback((e) => {
    if (preventDefault) e.preventDefault();
    
    // Cancel long press timer
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    if (!touchStart || !touchEnd) {
      // Handle tap
      if (onTap && touchStart) {
        const tapDuration = Date.now() - touchStart.time;
        if (tapDuration < 200) { // Quick tap
          onTap(e);
        }
      }
      return;
    }

    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = touchStart.y - touchEnd.y;
    const deltaTime = touchEnd.time - touchStart.time;

    // Determine swipe direction
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal swipe
      if (Math.abs(deltaX) > swipeThreshold && deltaTime < 500) {
        if (deltaX > 0 && onSwipeLeft) {
          onSwipeLeft(e);
        } else if (deltaX < 0 && onSwipeRight) {
          onSwipeRight(e);
        }
      }
    } else {
      // Vertical swipe
      if (Math.abs(deltaY) > swipeThreshold && deltaTime < 500) {
        if (deltaY > 0 && onSwipeUp) {
          onSwipeUp(e);
        } else if (deltaY < 0 && onSwipeDown) {
          onSwipeDown(e);
        }
      }
    }

    // Haptic feedback for swipes
    if (Math.abs(deltaX) > swipeThreshold || Math.abs(deltaY) > swipeThreshold) {
      if ('vibrate' in navigator) {
        navigator.vibrate(30);
      }
    }

    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onTap, swipeThreshold, longPressTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  return {
    elementRef,
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd
    }
  };
};

/**
 * Hook for mobile viewport handling
 */
export const useMobileViewport = () => {
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024,
    orientation: window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setViewport({
        width,
        height,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024,
        orientation: height > width ? 'portrait' : 'landscape'
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return viewport;
};

/**
 * Hook for mobile performance optimization
 */
export const useMobilePerformance = () => {
  const [isLowEnd, setIsLowEnd] = useState(false);
  const [connectionType, setConnectionType] = useState('unknown');

  useEffect(() => {
    // Detect low-end device
    const checkLowEnd = () => {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
          setIsLowEnd(renderer.includes('Mali') || renderer.includes('Adreno 3'));
        }
      }
    };

    // Detect connection type
    const checkConnection = () => {
      if ('connection' in navigator) {
        const connection = navigator.connection;
        setConnectionType(connection.effectiveType || 'unknown');
      }
    };

    checkLowEnd();
    checkConnection();

    // Listen for connection changes
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', checkConnection);
      return () => {
        navigator.connection.removeEventListener('change', checkConnection);
      };
    }
  }, []);

  return {
    isLowEnd,
    connectionType,
    shouldReduceAnimations: isLowEnd || connectionType === 'slow-2g' || connectionType === '2g'
  };
};

/**
 * Hook for mobile keyboard handling
 */
export const useMobileKeyboard = () => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const initialHeight = window.innerHeight;
      const currentHeight = window.innerHeight;
      const heightDifference = initialHeight - currentHeight;

      if (heightDifference > 150) {
        setIsKeyboardOpen(true);
        setKeyboardHeight(heightDifference);
      } else {
        setIsKeyboardOpen(false);
        setKeyboardHeight(0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    isKeyboardOpen,
    keyboardHeight
  };
};

export default useMobileGestures;
